import { useState, useRef, useEffect } from 'react'

// A compact ⋮ (kebab) button that opens a small popover of row actions.
// Used on friend rows so Block / Report / Remove-friend live inside the menu
// instead of as inline buttons that overflow the tile on long names.
//
// Props:
//   label    — the row subject's name, folded into the trigger's aria-label
//   actions  — [{ label, onSelect, danger? }]; onSelect fires the existing
//              handler UNCHANGED (confirms + toasts stay in the caller).
export default function FriendRowMenu({ label, actions = [] }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // Dismiss on outside tap/click and on Escape while the menu is open.
  useEffect(() => {
    if (!open) return
    const onPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label ? `More actions for ${label}` : 'More actions'}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 30, height: 30, lineHeight: 1, padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', color: 'var(--gray-400)',
          border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
          cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700,
        }}
      >
        ⋮
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
            minWidth: 150, padding: 4,
            background: 'var(--green-dark)',
            border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); a.onSelect() }}
              style={{
                width: '100%', textAlign: 'left',
                padding: '8px 10px', background: 'none',
                color: a.danger ? 'var(--gold)' : 'var(--cream)',
                border: 'none', borderRadius: 'calc(var(--radius) - 2px)',
                cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
